/**
 * Admin endpoints of the opt-in system.
 *
 * Mirrors `ADMIN · Opt-in` in the frozen contract. Every function calls the BFF unless this tab
 * asked for the fixtures with `?optInMock=` — see `OptInFeatures.mock.ts`, which is off by
 * default and cannot be committed on.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { OptInKeyHeldByDeletedError } from './OptInFeatures.errors';
import {
  isOptInMockEnabled,
  mockCreateOptInFeature,
  mockDeleteOptInFeature,
  mockDeleteOptInUser,
  mockPurgeOptInFeature,
  mockRestoreOptInFeature,
  mockGetOptInAdoption,
  mockGetOptInFeature,
  mockGetOptInFeatureUsers,
  mockListOptInFeatures,
  mockUpdateOptInFeature,
} from './OptInFeatures.mock';
import type {
  OptInAdoption,
  OptInFeature,
  OptInFeatureCreatePayload,
  OptInFeatureErrorResponse,
  OptInFeatureListRequest,
  OptInFeatureListResponse,
  OptInFeatureUpdatePayload,
  OptInFeatureUsersRequest,
  OptInFeatureUsersResponse,
  OptInPurgeResult,
  OptInUserErasure,
} from '../Types/OptInFeatures.types';
import { authenticatedFetch } from '@/Auth/services/auth.service';
import { env } from '@/config/env';
import { parseDeletedHolder } from '@/Shared/utils/deletedHolderConflict';

const OPT_IN_ENDPOINT = '/v1/opt-in-features';

export { OptInKeyHeldByDeletedError };

/**
 * Turns a failed response into an `Error` carrying the BFF's own message.
 *
 * The panel shows that message in the toast, so a 400 explaining which reason value is unknown
 * reaches the person who typed it instead of a generic phrase.
 */
const throwFromResponse = async (
  response: Response,
  fallback: string,
  byStatus: Record<number, string> = {},
): Promise<never> => {
  const errorData: Partial<OptInFeatureErrorResponse> = await response
    .json()
    .catch(() => ({ message: fallback, statusCode: response.status }));

  /*
    A `409` whose message names a DELETED feature is a different failure from one naming a live
    feature: the first has a way out — restore that row — and the second does not. An unreadable
    message falls through to the plain error, which is what the screen showed before.
  */
  if (response.status === 409) {
    const holder = parseDeletedHolder(errorData.message);

    if (holder?.kind === 'opt-in-feature') {
      throw new OptInKeyHeldByDeletedError(
        errorData.message ?? 'A chave pertence a uma funcionalidade excluída.',
        holder.id,
        holder.deletedAt,
      );
    }
  }

  if (byStatus[response.status]) throw new Error(byStatus[response.status]);

  throw new Error(errorData.message || fallback);
};

/**
 * Accepts both listing shapes.
 *
 * The contract does not pin the envelope of `GET /v1/opt-in-features`, and the other admin
 * listings in the BFF answer `{ data, pagination }`. A bare array is accepted too, so whichever
 * the module ships with, the screen does not break.
 */
const normalizeList = (payload: unknown): OptInFeatureListResponse => {
  if (Array.isArray(payload)) {
    return {
      data: payload as OptInFeature[],
      pagination: {
        page: 1,
        limit: payload.length,
        total: payload.length,
        totalPages: payload.length > 0 ? 1 : 0,
      },
    };
  }

  const envelope = payload as Partial<OptInFeatureListResponse>;
  const data = envelope.data ?? [];

  return {
    data,
    pagination: envelope.pagination ?? {
      page: 1,
      limit: data.length,
      total: data.length,
      totalPages: data.length > 0 ? 1 : 0,
    },
  };
};

export const getOptInFeatures = async (
  params: OptInFeatureListRequest = {},
): Promise<OptInFeatureListResponse> => {
  if (isOptInMockEnabled()) return mockListOptInFeatures(params);

  const queryParams = new URLSearchParams();
  /*
    Only sent when true. `includeDeleted=false` is the BFF's own default, and sending it anyway
    would put a parameter in every URL — including the cache key — for no change in behaviour.
  */
  if (params.includeDeleted) queryParams.append('includeDeleted', 'true');
  if (params.tenantSlug) queryParams.append('tenantSlug', params.tenantSlug);
  if (params.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
  if (params.page) queryParams.append('page', String(params.page));
  if (params.limit) queryParams.append('limit', String(params.limit));

  const queryString = queryParams.toString();
  const url = `${env.bffBaseURL}${OPT_IN_ENDPOINT}${queryString ? `?${queryString}` : ''}`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível carregar as funcionalidades', {
      403: 'Você não tem permissão para ver as funcionalidades opt-in',
    });
  }

  return normalizeList(await response.json());
};

export const getOptInFeatureById = async (id: string): Promise<OptInFeature> => {
  if (isOptInMockEnabled()) return mockGetOptInFeature(id);

  const response = await authenticatedFetch(`${env.bffBaseURL}${OPT_IN_ENDPOINT}/${id}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível carregar a funcionalidade', {
      403: 'Você não tem permissão para ver esta funcionalidade',
      404: 'Funcionalidade não encontrada',
    });
  }

  return response.json();
};

export const createOptInFeature = async (
  payload: OptInFeatureCreatePayload,
): Promise<OptInFeature> => {
  if (isOptInMockEnabled()) return mockCreateOptInFeature(payload);

  const response = await authenticatedFetch(`${env.bffBaseURL}${OPT_IN_ENDPOINT}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível criar a funcionalidade', {
      403: 'Você não tem permissão para criar funcionalidades opt-in',
      409: 'Já existe uma funcionalidade com esta chave neste tenant',
    });
  }

  return response.json();
};

/**
 * Saving an edit is **two calls**, because the BFF splits what the panel joins.
 *
 * `PATCH /:id` omits `translations` on purpose — the languages have their own route, `PUT
 * /:id/translations`, which REPLACES the whole set so the panel can express "this language is
 * gone". Sending them on the PATCH is a `400 property translations should not exist`: the global
 * pipe runs with `forbidNonWhitelisted`, so an undeclared field is refused rather than ignored.
 *
 * Creating is one call, because `POST` does accept them inline. The asymmetry is the BFF's, and
 * it stops here: the form has one "Salvar", and it should not have to know.
 *
 * Order is PATCH then PUT. Either order can half-save on a network failure, and the honest thing
 * is to say which half landed — a generic "não foi possível salvar" would send someone to redo
 * work that is already stored.
 */
export const updateOptInFeature = async (
  id: string,
  payload: OptInFeatureUpdatePayload,
): Promise<OptInFeature> => {
  if (isOptInMockEnabled()) return mockUpdateOptInFeature(id, payload);

  const { translations, ...feature } = payload;

  const response = await authenticatedFetch(`${env.bffBaseURL}${OPT_IN_ENDPOINT}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(feature),
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível salvar a funcionalidade', {
      403: 'Você não tem permissão para editar esta funcionalidade',
      404: 'Funcionalidade não encontrada',
    });
  }

  const saved: OptInFeature = await response.json();

  if (!translations) return saved;

  const translationsResponse = await authenticatedFetch(
    `${env.bffBaseURL}${OPT_IN_ENDPOINT}/${id}/translations`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ translations }),
    },
  );

  if (!translationsResponse.ok) {
    await throwFromResponse(
      translationsResponse,
      'Os dados da funcionalidade foram salvos, mas as traduções não. Salve de novo.',
      {
        403: 'Você não tem permissão para editar esta funcionalidade',
        404: 'Funcionalidade não encontrada',
      },
    );
  }

  // The translations route answers with the whole feature, so this is the newer of the two.
  return translationsResponse.json();
};

export const deleteOptInFeature = async (id: string): Promise<void> => {
  if (isOptInMockEnabled()) return mockDeleteOptInFeature(id);

  const response = await authenticatedFetch(`${env.bffBaseURL}${OPT_IN_ENDPOINT}/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível excluir a funcionalidade', {
      403: 'Você não tem permissão para excluir esta funcionalidade',
      404: 'Funcionalidade não encontrada',
    });
  }
};

/**
 * Undoes the soft delete — `PATCH /:id/restore`, behind `opt_in_feature:update`.
 *
 * No new permission: whoever can delete can undo their own mistake. It answers with the whole
 * feature, in the listing's own shape, so the row can be put back without a refetch.
 *
 * **`isActive` comes back exactly as it was left** — a restore undoes the delete and nothing else.
 * Unlike a survey, a live feature does not put itself in front of anybody: it makes a toggle
 * visible again and gives each person back the choice they had already made, dates intact.
 */
export const restoreOptInFeature = async (id: string): Promise<OptInFeature> => {
  if (isOptInMockEnabled()) return mockRestoreOptInFeature(id);

  const response = await authenticatedFetch(`${env.bffBaseURL}${OPT_IN_ENDPOINT}/${id}/restore`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível restaurar a funcionalidade', {
      403: 'Você não tem permissão para restaurar funcionalidades',
      404: 'Funcionalidade não encontrada',
      409: 'Esta funcionalidade não está excluída — talvez alguém já a tenha restaurado.',
    });
  }

  return response.json();
};

/**
 * Erase every opt-in row and every toggle event of a feature — `POST /:id/purge`.
 *
 * Behind `opt_in_feature:purge`, and behind typing the key: the BFF compares `confirmKey` with
 * `===` and refuses with `400` having deleted nothing. Two consequences the screen has to have
 * stated before this is called: everybody currently using the feature falls back to the default
 * behaviour, and `lastEnabledAt` — the anchor a conditioned survey's delay counts from — goes with
 * the rows.
 *
 * Survey answers are **not** touched, so this reopens no survey for anybody.
 */
export const purgeOptInFeature = async (
  id: string,
  confirmKey: string,
): Promise<OptInPurgeResult> => {
  if (isOptInMockEnabled()) return mockPurgeOptInFeature(id, confirmKey);

  const response = await authenticatedFetch(`${env.bffBaseURL}${OPT_IN_ENDPOINT}/${id}/purge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirmKey }),
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível apagar os dados de uso', {
      400: 'A chave digitada não bate com a da funcionalidade. Nada foi apagado.',
      403: 'Você não tem permissão para apagar dados de opt-in. É opt_in_feature:purge, que só o super admin tem.',
      404: 'Funcionalidade não encontrada',
    });
  }

  return response.json();
};

/**
 * Erase one person's opt-in data on one feature — `DELETE /:id/users/:platformUserId` (R30).
 *
 * The `404` has **two causes**, and they are not the same news: the feature does not exist, or that
 * person has nothing on it. The BFF says which in its message, so it is read rather than flattened
 * into one sentence — "essa pessoa não tem dados aqui" is something to shrug at, while "a
 * funcionalidade não existe" means the screen is pointing at nothing.
 */
export const deleteOptInUser = async (
  id: string,
  platformUserId: number,
): Promise<OptInUserErasure> => {
  if (isOptInMockEnabled()) return mockDeleteOptInUser(id, platformUserId);

  const response = await authenticatedFetch(
    `${env.bffBaseURL}${OPT_IN_ENDPOINT}/${id}/users/${platformUserId}`,
    { method: 'DELETE', headers: { 'Content-Type': 'application/json' } },
  );

  if (!response.ok) {
    if (response.status === 404) {
      const body: { message?: string } = await response.json().catch(() => ({}));
      const isPerson = (body.message ?? '').includes('no opt-in data');

      throw new Error(
        isPerson
          ? `A pessoa ${platformUserId} não tem dados de opt-in nesta funcionalidade. Ou nunca ligou, ou alguém já apagou — recarregue a lista.`
          : 'Funcionalidade não encontrada',
      );
    }

    await throwFromResponse(response, 'Não foi possível apagar os dados desta pessoa', {
      403: 'Você não tem permissão para apagar dados de opt-in. É opt_in_feature:purge, que só o super admin tem.',
    });
  }

  return response.json();
};

export const getOptInAdoption = async (id: string): Promise<OptInAdoption> => {
  if (isOptInMockEnabled()) return mockGetOptInAdoption(id);

  /*
    `?language=pt` asked for explicitly: without it the opt-out reasons come back from the base
    column, in English, next to a screen written in Portuguese. The identifiers stay raw — a reason
    that left the catalogue shows its own code, and the bucket with no reason stays "Não informado".
  */
  const response = await authenticatedFetch(
    `${env.bffBaseURL}${OPT_IN_ENDPOINT}/${id}/adoption?language=pt`,
    { method: 'GET', headers: { 'Content-Type': 'application/json' } },
  );

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível carregar a adoção', {
      403: 'Você não tem permissão para ver a adoção',
      404: 'Funcionalidade não encontrada',
    });
  }

  return response.json();
};

export const getOptInFeatureUsers = async (
  id: string,
  params: OptInFeatureUsersRequest = {},
): Promise<OptInFeatureUsersResponse> => {
  if (isOptInMockEnabled()) return mockGetOptInFeatureUsers(id, params);

  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', String(params.page));
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.state) queryParams.append('state', params.state);

  const queryString = queryParams.toString();
  const url = `${env.bffBaseURL}${OPT_IN_ENDPOINT}/${id}/users${queryString ? `?${queryString}` : ''}`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível carregar os usuários', {
      403: 'Você não tem permissão para ver os usuários',
      404: 'Funcionalidade não encontrada',
    });
  }

  return response.json();
};
